import React, { useMemo } from 'react'
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native'
import moment from 'moment'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@src/components/base'
import {
  EhrAppointmentAttendanceChargeKind,
  EhrAppointmentKind,
  EhrAppointmentStateKind,
} from '@src/types'
import { useVideoCallDurations } from '@screens/NutritionistHub/hooks/useVideoCallDurations.ts'
import { VIDEO_CALL_CARD_TIME_FORMAT, VIDEO_CALL_CARD_DATE_FORMAT } from '@src/config/momentFormat'
import { getAbbreviatedCoachName } from '@src/utils/coach/getAbbreviatedCoachName'
import { AppointmentPaymentMethodChip } from '../NextCall/Cards/AppointmentPaymentMethodChip'
import CallDuration from './CallDuration'

export interface CallCardProps {
  label?: string | null
  title?: string | null
  meetingAt: string
  coachName?: string | null
  appointmentKind?: EhrAppointmentKind | null
  lastStateKind?: EhrAppointmentStateKind | null
  chargeKind?: EhrAppointmentAttendanceChargeKind
  recurring: boolean
  placeholder?: boolean
  style?: StyleProp<ViewStyle>
  onPress?: () => void
}

const stateKindCssLookup: {
  [key: string]: 'occurred' | 'cancelled' | 'rescheduled'
} = {
  [EhrAppointmentStateKind.Occurred]: 'occurred',
  [EhrAppointmentStateKind.Cancelled]: 'cancelled',
  [EhrAppointmentStateKind.LateCancellation]: 'cancelled',
  [EhrAppointmentStateKind.NoShow]: 'cancelled',
  [EhrAppointmentStateKind.Rescheduled]: 'rescheduled',
  [EhrAppointmentStateKind.PendingReschedule]: 'rescheduled',
}

const getStateText = (state: EhrAppointmentStateKind) => {
  switch (state) {
    case EhrAppointmentStateKind.Occurred:
      return 'Completed'
    case EhrAppointmentStateKind.NoShow:
      return 'No-show'
    case EhrAppointmentStateKind.Cancelled:
      return 'Cancelled'
    case EhrAppointmentStateKind.LateCancellation:
      return 'Late Cancellation'
    case EhrAppointmentStateKind.LateReschedule:
      return 'Late Reschedule'
    case EhrAppointmentStateKind.Rescheduled:
      return 'Rescheduled'
    case EhrAppointmentStateKind.PendingReschedule:
      return 'Rescheduled'
    default:
      return null
  }
}

export const CallCard = (props: CallCardProps) => {
  const {
    label: labelProp,
    title,
    meetingAt,
    lastStateKind,
    coachName,
    appointmentKind,
    chargeKind,
    recurring,
    style,
    onPress,
    placeholder,
  } = props
  const styles = useStyleSheet(themedStyles)
  const { getAppointmentDuration } = useVideoCallDurations()

  const duration = getAppointmentDuration(appointmentKind)

  const getStatusType = () => {
    if (placeholder) {
      return 'placeholder'
    }

    if (lastStateKind) {
      return stateKindCssLookup[lastStateKind]
    }

    return null
  }

  const statusStyles = getStatusStyles(getStatusType(), styles)

  const label = useMemo(() => {
    if (labelProp) {
      return labelProp
    }

    if (placeholder) {
      return 'Scheduling in progress...'
    }

    if (lastStateKind) {
      return getStateText(lastStateKind)
    }

    return null
  }, [labelProp, placeholder, lastStateKind])

  const abbreviatedCoachName = useMemo(() => getAbbreviatedCoachName(coachName), [coachName])

  return (
    <TouchableOpacity
      style={[styles.container, statusStyles.borderColor, style]}
      activeOpacity={onPress ? 0.2 : 1}
      accessibilityLabel={`Video call with ${abbreviatedCoachName} on ${moment(meetingAt).format(
        VIDEO_CALL_CARD_DATE_FORMAT,
      )} at ${moment(meetingAt).format(VIDEO_CALL_CARD_TIME_FORMAT)}`}
      onPress={onPress}
    >
      {label && (
        <Text type="regular" style={[styles.label, statusStyles.textColor]}>
          {label}
        </Text>
      )}

      <View style={styles.content}>
        <View style={styles.textContent}>
          <Text type="regular" bold={!placeholder} style={placeholder && styles.placeholder}>
            {title ? title : 'Video call'} {recurring && ' (Recurring)'}
          </Text>
          {!!abbreviatedCoachName && (
            <Text type="regular" style={[styles.coachName, placeholder && styles.placeholder]}>
              {abbreviatedCoachName}
            </Text>
          )}
        </View>
        <View style={[styles.titleActionsContainer, placeholder && { marginRight: 24 }]}>
          {!!chargeKind && (
            <AppointmentPaymentMethodChip lastStateKind={lastStateKind} chargeKind={chargeKind} />
          )}

          {onPress && <Icon name="dots-three-vertical" weight="bold" />}
        </View>
      </View>

      <CallDuration
        coachName={coachName}
        meetingAt={meetingAt}
        duration={duration}
        placeholder={placeholder}
      />
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  container: {
    borderLeftWidth: 3,
    borderColor: 'theme.success.base',
    paddingLeft: 12,
  },
  label: {
    marginBottom: 5,
  },
  content: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 5,
  },
  textContent: {
    flex: 1,
  },
  coachName: {
    marginTop: 3,
    color: 'theme.text.secondary',
  },
  occurred: {
    borderColor: 'theme.success.darkest',
    color: 'theme.success.darkest',
  },
  cancelled: {
    borderColor: 'theme.error.base',
    color: 'theme.error.darkest',
  },
  rescheduled: {
    borderColor: 'theme.tooltip.textSecondary',
    color: 'theme.text.secondary',
  },
  default: {
    color: 'theme.success.darkest',
  },
  placeholder: {
    borderColor: 'theme.tooltip.textSecondary',
    color: 'theme.text.tertiary',
  },
  time: {
    marginTop: 12,
    color: 'theme.text.secondary',
  },
  titleActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    display: 'flex',
  },
})

const getStatusStyles = (statusType: string | null, styles: any) => {
  const statusStyle = statusType ? styles[statusType] : null

  return {
    borderColor: statusStyle ? { borderColor: statusStyle.borderColor } : null,
    textColor: statusStyle ? { color: statusStyle.color } : styles.default,
  }
}
